/**
 * Canonical tack/jibe recognizer — used by the web app, Cloud Functions
 * (`recognizeManeuvers`), and GPX debug tools. Import via the `services`
 * package; do not duplicate this file under `functions/`.
 */
import DirectionUtil from "utils/DirectionUtil.js";
import {isSailingSport} from "utils/SportUtil.js";
import {
  TACK_JIBE_POINTS_TO_USE,
  TACK_JIBE_MIN_ANGLE,
  TACK_JIBE_MAX_ANGLE,
  TACK_JIBE_SUSTAINED_LAG_SEC,
  TACK_JIBE_SUSTAINED_DURATION_SEC,
  TACK_JIBE_SUSTAINED_MIN_ANGLE,
  TACK_JIBE_ARC_VERY_STRONG_DEGREES,
  TACK_JIBE_ARC_VERY_STRONG_MARGIN_DEGREES,
  TACK_JIBE_ARC_STRONG_DEGREES,
  TACK_JIBE_ARC_STRONG_MARGIN_DEGREES,
  TACK_JIBE_ARC_SAILING_VERY_STRONG_DEGREES,
  TACK_JIBE_ARC_SAILING_STRONG_DEGREES,
  TACK_JIBE_MIN_AXIS_APPROACH_DEGREES,
  TACK_JIBE_AXIS_EXEMPT_ANGLE_DEG,
  TACK_JIBE_SUCCESS_ANGLE_EXEMPT_DEG,
  TACK_JIBE_PHANTOM_WEAK_UNVERIFIED_MAX_ANGLE,
  TACK_JIBE_STRAIGHT_LEG_MATCH_DEG,
  TACK_JIBE_STRAIGHT_LEG_MAX_FRACTION,
  TACK_JIBE_TURN_DIVERGENCE_DEG,
  TACK_JIBE_TURN_CONVERGE_DEG,
  TACK_JIBE_TURN_STABLE_RUN,
  TACK_JIBE_WINDOW_MAX_EXPAND_BACKWARD,
  TACK_JIBE_WINDOW_MAX_EXPAND_FORWARD,
  TACK_JIBE_WINDOW_MIN_WIDTH,
  MIN_SPEED_FOR_BEARING_KMH,
  TACK_JIBE_MIN_SEPARATION,
  TACK_JIBE_STRONG_MANEUVER_ANGLE_DEG,
  MIN_SPEEDS_BY_SPORT,
  DUPLICATE_POINT_TOLERANCE,
  SPORT_TYPES
} from "constants/WindAnalysisConstants.js";
import type {
  TrackPoint,
  SportThresholds,
  CandidateManeuver,
  ManeuverOverride
} from "./TacksAndJibesRecognizerService.types.js";

export type {
  TrackPoint,
  SportThresholds,
  CandidateManeuver,
  ManeuverOverride
} from "./TacksAndJibesRecognizerService.types.js";

class Movement {
  windDirection: number;
  locations: TrackPoint[];
  startLocation: TrackPoint;
  endLocation: TrackPoint;
  movementType: string;
  pointsToUse: number;
  sport: number;
  angle: number;
  avgSpeed: number;
  entrySpeed: number;
  exitSpeed: number;
  successful: boolean;
  direction: string | null;
  startIndex: number;
  endIndex?: number;
  isStopAndRestart?: boolean;
  isOverride?: boolean;
  isUserDefined?: boolean;
  overrideId?: any;
  index?: number;

  constructor(
    windDirection: number,
    sport: number,
    locations: TrackPoint[] = [],
    movementType: string = "none",
    pointsToUse: number = TACK_JIBE_POINTS_TO_USE
  ) {
    this.windDirection = windDirection;
    this.locations = locations;
    this.startLocation = locations[0];
    this.endLocation = locations[locations.length - 1];
    this.movementType = movementType;
    this.pointsToUse = pointsToUse;
    this.sport = sport;
    this.angle = 0;
    this.avgSpeed = 0;
    this.entrySpeed = 0;
    this.exitSpeed = 0;
    this.successful = false;
    this.direction = null;
    this.startIndex = -1;
  }

  setLocs(locations: TrackPoint[]): void {
    this.locations = locations;
    this.removeDuplicatePoints();
    this.startLocation = this.locations[0];
    this.endLocation = this.locations[this.locations.length - 1];
  }

  removeDuplicatePoints(tolerance: number = 0.0): void {
    if (this.locations.length <= 1) return;

    const uniqueLocations = [this.locations[0]]; // Always keep the first point

    for (let i = 1; i < this.locations.length; i++) {
      const current = this.locations[i];
      const lastUnique = uniqueLocations[uniqueLocations.length - 1];

      const latDiff = Math.abs(current.latitude - lastUnique.latitude);
      const lonDiff = Math.abs(current.longitude - lastUnique.longitude);

      if (latDiff > tolerance || lonDiff > tolerance) {
        uniqueLocations.push(current);
      }
    }

    this.locations = uniqueLocations;
  }

  adjustWind(angle: number): number {
    const absAngle = Math.abs(angle);
    return absAngle > 180 ? absAngle - 180 : absAngle + 180;
  }

  calculateBearing(from: TrackPoint, to: TrackPoint): number {
    return DirectionUtil.getBearingValue(from.latitude, from.longitude, to.latitude, to.longitude);
  }

  calculateDirection(windDirection: number, initialBearing: number): string {
    // Normalize both angles to be within 0 and 360 degrees
    const normalizedFirstAngle = initialBearing % 360;
    const normalizedSecondAngle = windDirection % 360;

    // Calculate the relative angle of the second line with respect to the first line
    const relativeAngle = (normalizedSecondAngle - normalizedFirstAngle) % 360;

    // Adjust relativeAngle to be positive in the range of 0 to 360
    const adjustedRelativeAngle = relativeAngle >= 0 ? relativeAngle : relativeAngle + 360;

    // Determine the relative position
    if (adjustedRelativeAngle >= 0 && adjustedRelativeAngle < 180) {
      return "Starboard";
    } else if (adjustedRelativeAngle >= 180 && adjustedRelativeAngle < 360) {
      return "Portboard";
    } else {
      return "none";
    }
  }

  calculateSpeeds(): void {
    const n = this.locations.length;

    if (n > 3) {
      this.entrySpeed = this._timeWeightedAverageSpeed(this.locations.slice(0, 3));
      this.exitSpeed = this._timeWeightedAverageSpeed(this.locations.slice(-3));
    } else {
      this.entrySpeed = 0;
      this.exitSpeed = 0;
    }

    // avgSpeed represents the speed DURING the maneuver itself, so it is
    // computed over the points between the entry (first 3) and exit (last 3)
    // samples. Including entry/exit would let the approach and recovery
    // speeds dominate and mask how much speed was lost mid-turn.
    //
    // We use a time-weighted mean (Σ speed·Δt / Σ Δt = total distance /
    // total time) instead of a plain arithmetic mean because GPS samples
    // are not always evenly spaced - e.g. devices with smart recording or
    // dropouts produce 2-5 s gaps that would otherwise be weighted the
    // same as a 1 s sample and skew the result.
    //
    // For windows too short to drop both ends (≤ 6 points - only reachable
    // via very small user overrides; auto-detection uses 10 points) we
    // fall back to the full window so we never report 0.
    const interior = n > 6 ? this.locations.slice(3, -3) : this.locations;
    this.avgSpeed = this._timeWeightedAverageSpeed(interior);
  }

  _arithmeticAverageSpeed(points: TrackPoint[]): number {
    if (!points || points.length === 0) return 0;
    return points.reduce((sum, loc) => sum + (loc.speedInKMH || 0), 0) / points.length;
  }

  _timeWeightedAverageSpeed(points: TrackPoint[]): number {
    if (!points || points.length === 0) return 0;
    if (points.length === 1) return points[0].speedInKMH || 0;

    let totalDt = 0;
    let weighted = 0;
    for (let i = 0; i < points.length - 1; i++) {
      const tA = points[i].unixTimestamp;
      const tB = points[i + 1].unixTimestamp;
      const dt = tA != null && tB != null ? tB - tA : 0;
      if (dt > 0) {
        const segMean = ((points[i].speedInKMH || 0) + (points[i + 1].speedInKMH || 0)) * 0.5;
        weighted += segMean * dt;
        totalDt += dt;
      }
    }

    if (totalDt > 0) return weighted / totalDt;
    // No usable timestamps (missing, equal, or out of order) - degrade
    // gracefully to the arithmetic mean rather than reporting 0.
    return this._arithmeticAverageSpeed(points);
  }

  checkWindCrossing(initialBearing: number, finalBearing: number, windDirection: number): boolean {
    const initialRelWind = this.calculateRelativeAngle(initialBearing, windDirection);
    const finalRelWind = this.calculateRelativeAngle(finalBearing, windDirection);
    return initialRelWind * finalRelWind < 0; // Crosses the wind
  }

  calculateRelativeAngle(bearing: number, windDirection: number): number {
    const angle = (bearing - windDirection + 360) % 360;
    return angle > 180 ? angle - 360 : angle;
  }

  normalize(angle: number): number {
    let x = angle % 360.0;
    if (x < 0) {
      x += 360.0;
    }
    return x;
  }

  // Absolute angular difference between two bearings, in [0, 180].
  // NOTE: a previous version of this function additionally folded near-180°
  // differences down to near-0° (treating bearings as line orientations).
  // That caused real head-to-wind tacks (where the rider's bearing reverses by
  // ~180°) to report angle ≈ 0° and fail the TACK_JIBE_MIN_ANGLE gate,
  // i.e. clear tacks were silently dropped.
  angleDifference(dirA: number, dirB: number): number {
    const a = this.normalize(dirA);
    const b = this.normalize(dirB);
    let diff = Math.abs(a - b);
    if (diff > 180) {
      diff = 360 - diff;
    }
    return diff;
  }

  // Distance between two compass bearings on the [0, 180] semicircle.
  // Used by classifyMovement instead of `((x + 180) % 360) - 180` math, which
  // is broken in JS because `%` returns a result with the sign of the
  // dividend: `(-300 % 360) === -300`, not `60`. With negative inputs the old
  // helper could return values up to 360, which made the supposedly-dead
  // `directionVsWind >= 295` clause trigger on real upwind segments and
  // misclassify small upwind course corrections as jibes.
  bearingDistance(a: number, b: number): number {
    const x = this.normalize(a);
    const y = this.normalize(b);
    let d = Math.abs(x - y);
    if (d > 180) d = 360 - d;
    return d;
  }

  // Shortest signed angular displacement from `a` to `b`, in (-180, 180].
  // Positive = clockwise, negative = counter-clockwise.
  signedBearingDelta(a: number, b: number): number {
    const x = this.normalize(a);
    const y = this.normalize(b);
    let d = (y - x) % 360;
    if (d > 180) d -= 360;
    if (d <= -180) d += 360;
    return d;
  }

  // Compute the heading the rider actually passed through at the half-way
  // point of the maneuver. Two candidates exist for any (init, fin) pair:
  //   shortMid = init + shortDelta/2       (the shorter, ≤180° rotation)
  //   longMid  = init + longDelta/2        (the longer,  >180° rotation)
  // We pick whichever is closer to the observed mid-window sample bearing,
  // which tells us which way the rider physically rotated. Neither candidate
  // works alone:
  //   - shortMid alone is wrong when the rider took the >180° rotation
  //   - the sample bearing alone is wrong when most of the rotation happens
  //     in the last few samples (then it captures the entry heading, not
  //     the mid-rotation heading).
  computePathMid(initialBearing: number, finalBearing: number, directionBearing: number): number {
    const shortDelta = this.signedBearingDelta(initialBearing, finalBearing);
    const sign = shortDelta >= 0 ? 1 : -1;
    const longDelta = shortDelta - sign * 360;
    const shortMid = this.normalize(initialBearing + shortDelta / 2);
    const longMid = this.normalize(initialBearing + longDelta / 2);
    const distShort = this.bearingDistance(shortMid, directionBearing);
    const distLong = this.bearingDistance(longMid, directionBearing);
    return distShort <= distLong ? shortMid : longMid;
  }

  classifyMovement(
    initialBearing: number,
    finalBearing: number,
    windDirection: number,
    directionBearing: number
  ): void {
    // `windDirection` here is the adjusted (leeward) direction.
    //
    // Tack vs jibe is decided by which side of the wind axis the rider's path
    // swept through:
    //   - through windward (upwind) arc → tack
    //   - through leeward  (downwind) arc → jibe
    //
    // We use a path-midpoint reconciled with the GPS samples (see
    // `computePathMid`) instead of either the raw sample bearing or the
    // shortest-path midpoint alone, both of which fail in different cases.
    //
    // The recognizer applies an additional arc-sample override after
    // `parseMovements()` (see `reclassifyByArcSamples`) which corrects the
    // ~5% of short, fast maneuvers where the 3-bearing path-mid lands on
    // the wrong side of the wind axis.
    const pathMid = this.computePathMid(initialBearing, finalBearing, directionBearing);
    const pathMidVsLeeward = this.bearingDistance(pathMid, windDirection);
    const initVsLeeward = this.bearingDistance(initialBearing, windDirection);
    const finalVsLeeward = this.bearingDistance(finalBearing, windDirection);

    if (isSailingSport(this.sport)) {
      // Sailing boats almost never tack through dead downwind, so a path
      // whose middle is clearly downwind (< 75° from leeward) is a jibe.
      if (pathMidVsLeeward < 75) {
        this.movementType = "jibe";
      } else {
        this.movementType = "tack";
      }
    } else {
      // For wing/wind/foil/kite, classify primarily by where the rider went
      // through the maneuver. As a secondary check, if both legs are clearly
      // downwind (< 60° from leeward), we still call it a jibe even if the
      // mid-direction is noisy.
      if (pathMidVsLeeward < 90 || (initVsLeeward < 60 && finalVsLeeward < 60)) {
        this.movementType = "jibe";
      } else {
        this.movementType = "tack";
      }
    }
  }

  getSportThresholds(sport: number): SportThresholds {
    if (SPORT_TYPES.SAILING.includes(sport)) return MIN_SPEEDS_BY_SPORT.SAILING;
    if (SPORT_TYPES.WINDSURF.includes(sport)) return MIN_SPEEDS_BY_SPORT.WINDSURF;
    if (SPORT_TYPES.WING.includes(sport)) return MIN_SPEEDS_BY_SPORT.WING;
    if (SPORT_TYPES.KITE.includes(sport)) return MIN_SPEEDS_BY_SPORT.KITE;
    if (SPORT_TYPES.FOIL.includes(sport)) return MIN_SPEEDS_BY_SPORT.FOIL;
    return MIN_SPEEDS_BY_SPORT.DEFAULT;
  }

  // Detection-layer speed gate. Rejects windows that cannot be a real maneuver
  // attempt:
  //   1) Entry must clear the sport's `exitSpeed` floor — filters out
  //      water-starts (entry ≈ 0) where the rider was not on the board yet.
  //      Exit is intentionally NOT gated here: a missed maneuver often ends
  //      in a crash (exit ≈ 0) and must still be counted for % missed stats.
  //   2) AT LEAST ONE end must be at riding speed (>= the sport's `avgSpeed`
  //      threshold) — filters out near-shore pivots, water-start wobbles and
  //      drift "tacks" at walking pace. A genuine missed maneuver always has
  //      a high-speed entry (rider committed) or a high-speed exit (rider
  //      recovered), so this still keeps unsuccessful-but-real attempts.
  // Without (2), riders doing low-speed practice / falls / pivots near shore
  // produce a cluster of "missed tacks" at the same lat/lon — exactly the
  // duplicated icons users see stacked near the beach in the activity map.
  meetsDetectionMinSpeed(sport: number): boolean {
    const thresholds = this.getSportThresholds(sport);
    const notWaterStart = this.entrySpeed >= thresholds.exitSpeed;
    const wasRiding = Math.max(this.entrySpeed, this.exitSpeed) >= thresholds.avgSpeed;
    return notWaterStart && wasRiding;
  }

  isSuccessful(sport: number): boolean {
    const thresholds = this.getSportThresholds(sport);
    if (this.avgSpeed > thresholds.avgSpeed && this.exitSpeed > thresholds.exitSpeed) {
      return true;
    }
    // Slow-through-turn: rider loses speed mid-maneuver but still completes a
    // real heading change on at least one leg at riding speed.
    return (
      this.angle >= TACK_JIBE_SUCCESS_ANGLE_EXEMPT_DEG &&
      this.exitSpeed >= thresholds.exitSpeed &&
      Math.max(this.entrySpeed, this.exitSpeed) >= thresholds.avgSpeed
    );
  }

  typeOfMovement(): string | null {
    if (this.locations.length < this.pointsToUse) return null;

    if (this.pointsToUse < 9) return null;

    const adjustedWindDirection = this.adjustWind(this.windDirection);

    let initialBearing = this.calculateBearing(this.locations[0], this.locations[Math.floor(this.pointsToUse / 3) - 1]);
    let finalBearing = this.calculateBearing(
      this.locations[this.pointsToUse - Math.floor(this.pointsToUse / 3)],
      this.locations[this.pointsToUse - 1]
    );

    const directionBearing = this.calculateBearing(
      this.locations[Math.floor(this.pointsToUse / 3) - 1],
      this.locations[this.pointsToUse - Math.floor(this.pointsToUse / 3)]
    );

    if (isSailingSport(this.sport)) {
      initialBearing = this.calculateBearing(this.locations[0], this.locations[Math.floor(this.pointsToUse / 2) - 1]);
      finalBearing = this.calculateBearing(
        this.locations[this.pointsToUse - Math.floor(this.pointsToUse / 2)],
        this.locations[this.pointsToUse - 1]
      );
    }

    this.angle = this.angleDifference(initialBearing, finalBearing);
    this.calculateSpeeds();

    if (this.avgSpeed < 1) {
      return "none";
    }

    // Reject water-starts, falls and drifting wobbles before they reach the
    // wind-crossing classifier. Without this gate, segments where the rider
    // is not actually riding produce false-positive tacks/jibes that show up
    // as "missed" maneuvers in the UI even though they are not maneuvers at all.
    if (!this.meetsDetectionMinSpeed(this.sport)) {
      return "none";
    }

    if (this.angle > TACK_JIBE_MIN_ANGLE && this.angle < TACK_JIBE_MAX_ANGLE) {
      const crossing = this.checkWindCrossing(initialBearing, finalBearing, adjustedWindDirection);

      const directionInitial = this.calculateDirection(adjustedWindDirection, initialBearing);
      const directionFinal = this.calculateDirection(adjustedWindDirection, finalBearing);

      if (crossing && directionFinal !== directionInitial) {
        this.classifyMovement(initialBearing, finalBearing, adjustedWindDirection, directionBearing);

        if (this.movementType === "jibe") {
          if (directionInitial === "Portboard") {
            this.direction = "Starboard";
          } else {
            this.direction = "Portboard";
          }
        } else if (this.movementType === "tack") {
          if (directionInitial === "Starboard") {
            this.direction = "Starboard";
          } else {
            this.direction = "Portboard";
          }
        }
        this.successful = this.isSuccessful(this.sport);
        return this.movementType;
      }
    }
    return "none";
  }
}

export class TacksAndJibesRecognizer {
  locations: TrackPoint[];
  sport: number;
  pointsToUse: number;
  windDirection: number;
  windDirectionWasInferred: boolean;
  assignedWindDirection: number | null | undefined;
  movements: Movement[];

  constructor(locations: TrackPoint[], windDirection: number | null | undefined, sport: number) {
    this.locations = locations;
    this.removeDuplicatePoints();
    this.sport = sport;
    this.pointsToUse = TACK_JIBE_POINTS_TO_USE;

    // Resolve the wind direction we will actually run detection with.
    // This handles two failure modes that otherwise produce broken counts:
    //   1) `windDirection === 0` (or null/undefined) is the default sentinel
    //      from ACTIVITY_DEFAULT_VALUES — the wind-data fetch never ran or
    //      failed silently. Treating 0 as a literal "wind from N" silently
    //      breaks detection on every session that didn't get wind data.
    //   2) An assigned wind direction that is grossly inconsistent with the
    //      track. The recognizer's crossing test is binary, so a wind angle
    //      off by more than ~30° collapses detection.
    // The inference scans high-quality candidate maneuvers (independent of
    // wind direction) and picks the wind axis that best explains them. The
    // assigned value wins in any ambiguous case — we only override when the
    // data tells a clearly different story.
    const resolution = this.resolveWindDirection(windDirection);
    this.windDirection = resolution.windDirection;
    this.windDirectionWasInferred = resolution.inferred;
    this.assignedWindDirection = windDirection;

    this.movements = this.parseMovements();

    // Sustained-heading post-filter. The 10-point in-window detector is
    // sensitive enough that GPS noise on a near-straight upwind leg can
    // trigger a phantom tack/jibe (small wiggle that happens to straddle
    // the wind axis). We reject those by requiring a real, sustained
    // heading change on the legs around each detected maneuver. See
    // `filterPhantomManeuvers` for details.
    this.movements = this.filterPhantomManeuvers(this.movements);

    // No-axis-crossing filter. A real tack sweeps the rider's heading
    // through windward (head-to-wind); a real jibe sweeps through leeward
    // (dead-downwind). If the rider's heading never approaches either
    // axis during the window, they didn't cross the wind — what the
    // detector caught is a stall, a crash, or a "bear away" sail change.
    // See `filterNonCrossingManeuvers` and
    // `TACK_JIBE_MIN_AXIS_APPROACH_DEGREES` for the threshold.
    this.movements = this.filterNonCrossingManeuvers(this.movements);

    // Arc-sample reclassification. The 3-bearing path-mid classifier in
    // `Movement.classifyMovement` gets the wrong axis for ~5% of short,
    // fast maneuvers (notably wing/wind jibes where the rider's turn is
    // over in 3-4 GPS samples and the in-window mid bearing is dominated
    // by pre- or post-leg). This pass walks the full track around each
    // maneuver, finds the closest approach to each wind axis, and flips
    // the type when the arc evidence is strong and asymmetric. See
    // `reclassifyByArcSamples` for the heuristic and
    // `WindAnalysisConstants.TACK_JIBE_ARC_STRONG_DEGREES` for thresholds.
    this.reclassifyByArcSamples(this.movements);

    // Upwind-beat fix-up: a high-angle jibe sandwiched between two tacks on
    // a board sport is almost always a misclassified tack (GPS apex noise).
    this._reclassifyJibesBetweenTacks(this.movements);
    this._refreshSuccessFlags();

    // Window alignment. The 10-point detection window is rarely the
    // actual U-turn boundary; for short maneuvers it pads the U with
    // pre-leg samples, for long maneuvers it slices the middle. The
    // user-visible result is the marker / highlighted segment landing
    // a few points before (or after) the actual carve. This pass walks
    // outward from the detection window and snaps start/end to the
    // points where the rider's heading leaves the pre-leg and settles
    // on the post-leg. See `alignManeuverWindowsToTurn` for the full
    // rationale and `tools/measure-window-skew.mjs` for empirical
    // measurements of the misalignment.
    this.alignManeuverWindowsToTurn(this.movements);

    // Stop-and-restart detection. The 10-point sliding window in
    // parseMovements() can't span a multi-minute pause, so any 180° turn
    // where the rider stopped (fell, swam, water-started) goes undetected
    // even though the user clearly performed a maneuver. This pass adds
    // those without disturbing the regular detection.
    const bridgeMovements = this.findStopAndRestartManeuvers();
    if (bridgeMovements.length > 0) {
      this.movements = this.movements.concat(bridgeMovements).sort((a, b) => (a.startIndex ?? 0) - (b.startIndex ?? 0));
    }
  }

  // Identifies windows that look like real maneuvers regardless of wind
  // direction: large angle change AND speeds that pass the sport's exit-speed
  // floor on both ends (so water-starts, falls and drifting are excluded).
  // The result feeds wind-axis inference.
  findCandidateManeuvers(): CandidateManeuver[] {
    const candidates = [];
    if (!this.locations || this.locations.length < this.pointsToUse) return candidates;
    // Sport thresholds via a temporary Movement (kept here so we don't
    // duplicate the SPORT_TYPES → MIN_SPEEDS_BY_SPORT lookup).
    const probe = new Movement(0, this.sport);
    const thresholds = probe.getSportThresholds(this.sport);
    const ptsThird = Math.floor(this.pointsToUse / 3);
    // 60° is conservative: real tacks/jibes are always >60°, while course
    // corrections and GPS noise are typically below it.
    const MIN_CANDIDATE_ANGLE = 60;
    for (let i = 0; i + this.pointsToUse <= this.locations.length; i++) {
      const win = this.locations.slice(i, i + this.pointsToUse);
      const init = DirectionUtil.getBearingValue(
        win[0].latitude,
        win[0].longitude,
        win[ptsThird - 1].latitude,
        win[ptsThird - 1].longitude
      );
      const fin = DirectionUtil.getBearingValue(
        win[this.pointsToUse - ptsThird].latitude,
        win[this.pointsToUse - ptsThird].longitude,
        win[this.pointsToUse - 1].latitude,
        win[this.pointsToUse - 1].longitude
      );
      const dirB = DirectionUtil.getBearingValue(
        win[ptsThird - 1].latitude,
        win[ptsThird - 1].longitude,
        win[this.pointsToUse - ptsThird].latitude,
        win[this.pointsToUse - ptsThird].longitude
      );
      const ni = ((init % 360) + 360) % 360;
      const nf = ((fin % 360) + 360) % 360;
      let ang = Math.abs(ni - nf);
      if (ang > 180) ang = 360 - ang;
      if (ang < MIN_CANDIDATE_ANGLE) continue;
      const last = this.pointsToUse - 1;
      const avg = win.reduce((s, p) => s + p.speedInKMH, 0) / this.pointsToUse;
      const entry = (win[0].speedInKMH + win[1].speedInKMH + win[2].speedInKMH) / 3;
      const exit = (win[last].speedInKMH + win[last - 1].speedInKMH + win[last - 2].speedInKMH) / 3;
      if (avg < thresholds.avgSpeed) continue;
      if (entry < thresholds.exitSpeed || exit < thresholds.exitSpeed) continue;
      candidates.push({init, fin, dirB});
    }
    return candidates;
  }

  // Score how well a given wind direction explains a list of candidate
  // maneuvers. A maneuver is "explained" when its bearings straddle the
  // leeward axis (so it would pass the recognizer's wind-crossing test)
  // AND it falls cleanly into either a tack or a jibe rather than landing
  // in the ambiguous beam-reach band. Returns a count in [0, candidates.length].
  scoreWindDirection(candidates: CandidateManeuver[], windDirection: number): number {
    const adj = Math.abs(windDirection) > 180 ? Math.abs(windDirection) - 180 : Math.abs(windDirection) + 180;
    const norm = (a) => ((a % 360) + 360) % 360;
    const dist = (a, b) => {
      let d = Math.abs(norm(a) - norm(b));
      if (d > 180) d = 360 - d;
      return d;
    };
    const rel = (b, w) => {
      const a = (b - w + 360) % 360;
      return a > 180 ? a - 360 : a;
    };
    let score = 0;
    for (const c of candidates) {
      // Must straddle leeward axis to pass crossing test
      if (rel(c.init, adj) * rel(c.fin, adj) >= 0) continue;
      // And must look like either a tack (path through windward) or a jibe
      // (path through leeward), i.e. the mid-direction is not on the beam
      // (45-135° away from the wind axis).
      const dvL = dist(c.dirB, adj);
      if (dvL < 60 || dvL > 120) score++;
    }
    return score;
  }

  inferWindDirection(candidates: CandidateManeuver[]): number | null {
    if (candidates.length < 5) return null;
    let best = {dir: 0, score: -1};
    for (let wd = 0; wd < 360; wd += 5) {
      const score = this.scoreWindDirection(candidates, wd);
      if (score > best.score) best = {dir: wd, score};
    }
    // Require at least half of the candidates to be explained, otherwise the
    // data is too noisy to trust the inference.
    if (best.score < candidates.length / 2) return null;
    return best.dir;
  }

  resolveWindDirection(assignedWindDirection: number | null | undefined): {windDirection: number; inferred: boolean} {
    const isUnset = assignedWindDirection == null || assignedWindDirection === 0;
    const candidates = this.findCandidateManeuvers();
    const inferred = this.inferWindDirection(candidates);

    // No candidates / can't infer → trust whatever was passed in.
    if (inferred == null) {
      return {windDirection: assignedWindDirection ?? 0, inferred: false};
    }

    if (isUnset) {
      return {windDirection: inferred, inferred: true};
    }

    // Both are set: only override if the assigned value is a poor fit AND
    // the inferred one is a much better fit.
    const assignedScore = this.scoreWindDirection(candidates, assignedWindDirection);
    const inferredScore = this.scoreWindDirection(candidates, inferred);
    const totalCandidates = candidates.length;
    const tooFewExplained = assignedScore < totalCandidates / 3;
    const inferredIsMuchBetter = inferredScore >= assignedScore * 2 && inferredScore >= totalCandidates / 2;

    if (tooFewExplained && inferredIsMuchBetter) {
      return {windDirection: inferred, inferred: true};
    }
    return {windDirection: assignedWindDirection, inferred: false};
  }

  /**
   * Bearing of a "stable leg" on one side of a maneuver edge.
   *
   * Walks `lagSec` seconds away from `centerIdx` in `dir` (-1 = backwards,
   * +1 = forwards) to skip the maneuver's own arc dynamics, then collects
   * up to `durSec` seconds of "riding" samples (speed >= `ridingKmh`).
   * Returns the bearing from the first to the last collected sample, or
   * `null` when there aren't enough riding samples to be reliable (e.g.
   * the maneuver is at the start/end of the track, or the leg is too short
   * after filtering out non-riding samples).
   *
   * Why a fixed lag-then-window instead of just averaging the immediate
   * neighborhood: during a turn the per-point bearing rotates by tens of
   * degrees per second, so a window touching the maneuver dilutes the
   * "true leg" heading. Skipping `lagSec` and then sampling for `durSec`
   * gives a clean leg bearing.
   *
   * Optional `boundaryIdx` clamps the search so the leg measurement does
   * not extend across an adjacent detected maneuver. This is what lets
   * tight back-to-back jibes (e.g. wing/wingfoil slalom in front of the
   * beach, where the inter-maneuver leg is only 6-10 s long) be measured
   * correctly — without it, the fixed 15 s measurement window bleeds
   * across the next maneuver and the pre/post bearings average out to
   * essentially the same direction, causing real maneuvers to be silently
   * dropped by the phantom filter. When the available leg is shorter than
   * `lagSec + ~5 s`, the lag is reduced (down to a 1 s floor) so we still
   * get 3+ collectable samples; if even that yields too little data, the
   * function returns `null` and the caller abstains (keeping the
   * maneuver) per the same conservative rule used at start/end of track.
   *
   * @param {number} centerIdx - point index on the maneuver edge
   * @param {number} dir - direction to walk (-1 = pre, +1 = post)
   * @param {number} lagSec - seconds to skip near the maneuver edge
   * @param {number} durSec - seconds of riding samples to collect after the lag
   * @param {number} ridingKmh - minimum speed (km/h) to count a sample
   * @param {number|null} boundaryIdx - inclusive index of the adjacent
   *   maneuver's nearest edge; the leg search stops one sample before it.
   *   `null` means no clamp (the historical isolated-maneuver case).
   * @return {number|null} bearing in [0, 360) or null when undecidable
   */
  sustainedLegBearing(
    centerIdx: number,
    dir: number,
    lagSec: number,
    durSec: number,
    ridingKmh: number,
    boundaryIdx: number | null = null
  ): number | null {
    const pts = this.locations;
    if (!pts || centerIdx < 0 || centerIdx >= pts.length) return null;
    const t0 = pts[centerIdx].unixTimestamp;
    if (t0 == null) return null;

    // If the neighbor is close, shrink the lag so at least ~5 s remain for
    // sample collection after the lag — otherwise the lag eats the whole
    // available leg and we return null on real maneuvers.
    let effectiveLag = lagSec;
    if (boundaryIdx != null && boundaryIdx >= 0 && boundaryIdx < pts.length) {
      const tBoundary = pts[boundaryIdx].unixTimestamp;
      if (tBoundary != null) {
        const availableSec = Math.abs(tBoundary - t0);
        const minCollectTime = 5;
        if (availableSec - effectiveLag < minCollectTime) {
          effectiveLag = Math.max(1, availableSec - minCollectTime);
        }
      }
    }

    const beyondBoundary = (idx) => {
      if (boundaryIdx == null) return false;
      return dir < 0 ? idx <= boundaryIdx : idx >= boundaryIdx;
    };

    const withinLag = (idx) => Math.abs(pts[idx].unixTimestamp - t0) < effectiveLag;
    const withinWindow = (idx) => Math.abs(pts[idx].unixTimestamp - t0) <= effectiveLag + durSec;
    let j = centerIdx;
    while (j >= 0 && j < pts.length && !beyondBoundary(j) && withinLag(j)) {
      j += dir;
    }
    const collected = [];
    while (j >= 0 && j < pts.length && !beyondBoundary(j) && withinWindow(j)) {
      if ((pts[j].speedInKMH || 0) >= ridingKmh) collected.push(pts[j]);
      j += dir;
    }
    if (collected.length < 3) return null;
    if (dir < 0) collected.reverse();
    const a = collected[0];
    const b = collected[collected.length - 1];
    return DirectionUtil.getBearingValue(a.latitude, a.longitude, b.latitude, b.longitude);
  }

  /**
   * Resolve a maneuver's effective end index. Some detector branches set
   * `endIndex` explicitly; others leave it `undefined` and the implicit
   * width comes from `Movement.locations.length` (defaulting to
   * `pointsToUse` when the movement has no captured slice).
   *
   * Centralized so every neighbor-bound calculation uses the same rule
   * (otherwise the phantom filter and the window-aligner can disagree on
   * where a previous maneuver "ends", which silently breaks the boundary
   * clamping for tight back-to-back maneuvers).
   *
   * @param {Movement|null|undefined} m - maneuver instance
   * @return {number|null} inclusive end index, or null when undecidable
   */
  _movementEndIndex(m: Movement | null | undefined): number | null {
    if (!m || m.startIndex == null) return null;
    if (m.endIndex != null) return m.endIndex;
    const width = m.locations?.length || this.pointsToUse;
    return m.startIndex + width - 1;
  }

  /**
   * Whether `m` was detected with enough in-window angle to be treated as
   * a real maneuver for neighbor-aware boundary clamping. See
   * `TACK_JIBE_STRONG_MANEUVER_ANGLE_DEG` for the threshold rationale.
   *
   * Used exclusively by `filterPhantomManeuvers` and
   * `alignManeuverWindowsToTurn` to skip past phantom neighbours when
   * looking for the next "real" anchor — so two adjacent phantoms can
   * each be measured against the surrounding (real) track instead of
   * shielding each other from the sustained-heading drop.
   *
   * Classification, success and per-sport thresholds are unaffected.
   *
   * @param {Movement|null|undefined} m
   * @return {boolean}
   */
  _isStrongManeuver(m: Movement | null | undefined): boolean {
    if (!m || m.startIndex == null) return false;
    return (m.angle || 0) >= TACK_JIBE_STRONG_MANEUVER_ANGLE_DEG;
  }

  /**
   * Walk away from index `i` and return the nearest neighbour with a
   * "strong" detected angle, skipping weak/phantom detections in
   * between. Returns null when no strong neighbour exists in that
   * direction.
   *
   * @param {Array<Movement>} movements
   * @param {number} i - centre index in `movements`
   * @param {number} dir - -1 = backwards (pre), +1 = forwards (post)
   * @return {Movement|null}
   */
  _nearestStrongNeighbor(movements: Movement[], i: number, dir: number): Movement | null {
    for (let k = i + dir; k >= 0 && k < movements.length; k += dir) {
      if (this._isStrongManeuver(movements[k])) return movements[k];
    }
    return null;
  }

  /**
   * Reject maneuvers whose pre/post sustained heading change is below
   * `TACK_JIBE_SUSTAINED_MIN_ANGLE`. See WindAnalysisConstants for the
   * full rationale. The filter abstains (keeps the maneuver) when either
   * leg cannot be measured reliably, so end-of-track maneuvers and
   * stop-and-restart cases are never accidentally dropped here.
   *
   * @param {Array<Movement>} movements - the output of `parseMovements`
   * @return {Array<Movement>} the same list with phantom maneuvers removed
   */
  filterPhantomManeuvers(movements: Movement[]): Movement[] {
    if (!movements || movements.length === 0) return movements;
    const probe = new Movement(this.windDirection, this.sport);
    const thresholds = probe.getSportThresholds(this.sport);
    const ridingKmh = thresholds.exitSpeed;
    return movements.filter((m, i) => {
      if (!m || m.startIndex == null) return true;
      const startIdx = m.startIndex;
      const endIdx = this._movementEndIndex(m);

      // Clamp the leg measurements so they cannot bleed across the
      // previous/next detected maneuvers. Without this, tight back-to-back
      // jibes (common in wing/wingfoil slalom) have their pre/post leg
      // windows cover the neighboring legs too — the displacement-based
      // "leg bearing" then averages out across multiple physical legs and
      // the filter falsely reads "no sustained heading change," dropping
      // a real maneuver. The clamp is one sample short of the neighbor's
      // edge (exclusive) so we never sample inside a turn.
      //
      // Critical refinement: only clamp against STRONG (real-angle)
      // neighbours — skipping past weak phantom detections to find the
      // next real anchor. Without this, two adjacent phantom detections
      // (GPS wiggle on a near-wind-axis straight line) clamp each
      // other's leg windows down to 0 samples, both abstain, and both
      // survive. With this, each phantom is measured against the
      // surrounding real track and the sustained-heading filter drops
      // them as it was meant to. Real back-to-back maneuvers always
      // carry strong detected angles (>= TACK_JIBE_STRONG_MANEUVER_ANGLE_DEG)
      // and continue to benefit from the clamp.
      const prevStrong = this._nearestStrongNeighbor(movements, i, -1);
      const nextStrong = this._nearestStrongNeighbor(movements, i, +1);
      const preBoundary = prevStrong ? this._movementEndIndex(prevStrong) : null;
      const postBoundary = nextStrong ? nextStrong.startIndex : null;

      const pre = this.sustainedLegBearing(
        startIdx - 1,
        -1,
        TACK_JIBE_SUSTAINED_LAG_SEC,
        TACK_JIBE_SUSTAINED_DURATION_SEC,
        ridingKmh,
        preBoundary
      );
      const post = this.sustainedLegBearing(
        endIdx + 1,
        +1,
        TACK_JIBE_SUSTAINED_LAG_SEC,
        TACK_JIBE_SUSTAINED_DURATION_SEC,
        ridingKmh,
        postBoundary
      );
      if (pre == null || post == null) {
        if ((m.angle || 0) < TACK_JIBE_PHANTOM_WEAK_UNVERIFIED_MAX_ANGLE && !this._isNearTrackBoundary(startIdx)) {
          return false;
        }
        return true;
      }
      if (this._isStraightLegPhantom(m, pre, probe) && !this._isSandwichedByTacks(movements, i)) return false;
      return probe.bearingDistance(pre, post) >= TACK_JIBE_SUSTAINED_MIN_ANGLE;
    });
  }

  /**
   * Whether `idx` sits near the start or end of the track where sustained-leg
   * measurements are often impossible. We abstain on weak detections there.
   *
   * @param {number} idx
   * @return {boolean}
   */
  _isNearTrackBoundary(idx: number): boolean {
    const pts = this.locations;
    if (!pts || pts.length === 0 || idx == null) return true;
    const t = pts[idx]?.unixTimestamp;
    if (t == null) return true;
    const startT = pts[0].unixTimestamp;
    const endT = pts[pts.length - 1].unixTimestamp;
    return t - startT < 60 || endT - t < 60;
  }

  /**
   * True when most of the detection window is still on the pre-leg heading —
   * the rider had not started turning yet and the endpoint angle is noise on
   * a straight reach.
   *
   * @param {Movement} m
   * @param {number} preLegBearing
   * @param {Movement} probe
   * @return {boolean}
   */
  _isStraightLegPhantom(m: Movement, preLegBearing: number, probe: Movement): boolean {
    const start = m.startIndex;
    const end = this._movementEndIndex(m);
    if (start == null || end == null) return false;
    let onLeg = 0;
    let total = 0;
    for (let i = start; i < end && i < this.locations.length - 1; i++) {
      const b = this.localBearingAt(i);
      if (b == null) continue;
      total += 1;
      if (probe.bearingDistance(b, preLegBearing) <= TACK_JIBE_STRAIGHT_LEG_MATCH_DEG) onLeg += 1;
    }
    if (total < 3) return false;
    return onLeg / total > TACK_JIBE_STRAIGHT_LEG_MAX_FRACTION;
  }

  /**
   * Whether maneuver `i` sits between two tack detections (upwind beat pattern).
   *
   * @param {Array<Movement>} movements
   * @param {number} i
   * @return {boolean}
   */
  _isSandwichedByTacks(movements: Movement[], i: number): boolean {
    if (!movements || i <= 0 || i >= movements.length - 1) return false;
    return movements[i - 1]?.movementType === "tack" && movements[i + 1]?.movementType === "tack";
  }

  /**
   * Recompute `successful` after post-filters and type fix-ups.
   */
  _refreshSuccessFlags(): void {
    if (!this.movements) return;
    for (const m of this.movements) {
      if (!m || m.isStopAndRestart) continue;
      m.successful = m.isSuccessful(this.sport);
    }
  }

  /**
   * Drop maneuvers whose heading never approaches the windward axis OR the
   * leeward axis within `TACK_JIBE_MIN_AXIS_APPROACH_DEGREES`. A real tack
   * crosses windward (heading ≈ wind-from); a real jibe crosses leeward
   * (heading ≈ wind-to). If neither axis is approached during the entire
   * detection window the rider didn't cross the wind — what the detector
   * caught is almost always a stall, a crash, or a "bear away" sail
   * change. The user-reported `Gybe controvento.gpx` (upwind jibe) and
   * `not a tack 0YAGLIlNzzSu6BixrX1TR4xByKX2.gpx` files are dominated by
   * this failure mode.
   *
   * Conservative: we drop only when BOTH axes are far. A maneuver that
   * dipped close to one axis stays, even if the apex bearing is noisy.
   *
   * @param {Array<Movement>} movements
   * @return {Array<Movement>}
   */
  filterNonCrossingManeuvers(movements: Movement[]): Movement[] {
    if (!movements || movements.length === 0) return movements;
    const pts = this.locations;
    if (!pts || pts.length < 2) return movements;
    const probe = new Movement(this.windDirection, this.sport);
    const leeAxis = probe.adjustWind(this.windDirection);
    const windAxis = probe.normalize(leeAxis + 180);
    const minApproach = TACK_JIBE_MIN_AXIS_APPROACH_DEGREES;

    return movements.filter((m) => {
      if (!m || m.startIndex == null) return true;
      if ((m.angle || 0) >= TACK_JIBE_AXIS_EXEMPT_ANGLE_DEG) return true;
      const startIdx = m.startIndex;
      const endIdx = m.endIndex != null ? m.endIndex : startIdx + (m.locations?.length || this.pointsToUse) - 1;
      const from = Math.max(0, startIdx - 1);
      const to = Math.min(pts.length - 1, endIdx + 1);

      let minVsLee = Infinity;
      let minVsWind = Infinity;
      let sampled = false;
      for (let i = from; i < to; i++) {
        const a = pts[i];
        const b = pts[i + 1];
        if (!a || !b) continue;
        const sA = a.speedInKMH != null ? a.speedInKMH : (a.speedInMPS || 0) * 3.6;
        const sB = b.speedInKMH != null ? b.speedInKMH : (b.speedInMPS || 0) * 3.6;
        if ((sA || 0) < MIN_SPEED_FOR_BEARING_KMH || (sB || 0) < MIN_SPEED_FOR_BEARING_KMH) continue;
        const bearing = DirectionUtil.getBearingValue(a.latitude, a.longitude, b.latitude, b.longitude);
        if (bearing == null) continue;
        sampled = true;
        const dLee = probe.bearingDistance(bearing, leeAxis);
        const dWind = probe.bearingDistance(bearing, windAxis);
        if (dLee < minVsLee) minVsLee = dLee;
        if (dWind < minVsWind) minVsWind = dWind;
      }
      // Abstain (keep) when we couldn't reliably sample any high-speed
      // bearings — we never want to drop a maneuver just because the
      // rider was slow at the entry/exit.
      if (!sampled) return true;
      return Math.min(minVsLee, minVsWind) <= minApproach;
    });
  }

  /**
   * Walks the track around each maneuver and overrides the path-mid tack/jibe
   * verdict when the rider's heading clearly passed through one wind axis
   * (and clearly not the other). Mutates each Movement's `movementType`.
   *
   * Why this exists: the in-window classifier only has three coarse bearings
   * (initial / mid / final) sampled from the 10-point detection window. For
   * short maneuvers (wing/wind jibes complete in ~3-4 samples), the "mid"
   * bearing is dominated by the pre- or post-leg and the reconciled path
   * mid-point lands on the wrong side of the wind axis. Sailing turns are
   * slow enough that this rarely matters; for wing/wind sports it accounts
   * for most of the "wrong tack, is jibe" reports.
   *
   * The check uses the full track, not just `Movement.locations`, because
   * the apex of a wing jibe often falls one or two samples beyond the
   * detection window (see `parseMovements` line 759, where the prev
   * maneuver's locations are shifted by -1 relative to its startIndex).
   *
   * Conservative override: we ONLY flip the type when the arc passes
   * within `TACK_JIBE_ARC_STRONG_DEGREES` of one wind axis AND at least
   * `TACK_JIBE_ARC_STRONG_MARGIN_DEGREES` farther from the other. That
   * keeps the existing baseline whenever the arc is ambiguous (e.g. a
   * near-180° spin that brushes both axes).
   *
   * @param {Array<Movement>} movements - mutated in place
   */
  reclassifyByArcSamples(movements: Movement[]): void {
    if (!movements || movements.length === 0) return;
    const pts = this.locations;
    if (!pts || pts.length < 2) return;
    const probe = new Movement(this.windDirection, this.sport);
    const leeAxis = probe.adjustWind(this.windDirection);
    const windAxis = probe.normalize(leeAxis + 180);
    const sailing = isSailingSport(this.sport);
    const veryStrongDeg = sailing ? TACK_JIBE_ARC_SAILING_VERY_STRONG_DEGREES : TACK_JIBE_ARC_VERY_STRONG_DEGREES;
    const veryStrongMargin = TACK_JIBE_ARC_VERY_STRONG_MARGIN_DEGREES;
    const strongDeg = sailing ? TACK_JIBE_ARC_SAILING_STRONG_DEGREES : TACK_JIBE_ARC_STRONG_DEGREES;
    const strongMargin = TACK_JIBE_ARC_STRONG_MARGIN_DEGREES;

    for (const m of movements) {
      if (!m || m.startIndex == null) continue;
      const startIdx = m.startIndex;
      const endIdx = m.endIndex != null ? m.endIndex : startIdx + (m.locations?.length || this.pointsToUse) - 1;
      // Expand the inspection window by one sample on each side. The
      // apex bearing often falls just outside the detection window
      // (`parseMovements` offsets locations by -1 in the prevMove path)
      // and clipping at `startIdx`/`endIdx` is what fooled the in-class
      // classifier in the first place.
      const from = Math.max(0, startIdx - 1);
      const to = Math.min(pts.length - 1, endIdx + 1);

      let minVsLee = Infinity;
      let minVsWind = Infinity;
      for (let i = from; i < to; i++) {
        const a = pts[i];
        const b = pts[i + 1];
        if (!a || !b) continue;
        const sA = a.speedInKMH != null ? a.speedInKMH : (a.speedInMPS || 0) * 3.6;
        const sB = b.speedInKMH != null ? b.speedInKMH : (b.speedInMPS || 0) * 3.6;
        if ((sA || 0) < MIN_SPEED_FOR_BEARING_KMH || (sB || 0) < MIN_SPEED_FOR_BEARING_KMH) continue;
        const bearing = DirectionUtil.getBearingValue(a.latitude, a.longitude, b.latitude, b.longitude);
        if (bearing == null) continue;
        const dLee = probe.bearingDistance(bearing, leeAxis);
        const dWind = probe.bearingDistance(bearing, windAxis);
        if (dLee < minVsLee) minVsLee = dLee;
        if (dWind < minVsWind) minVsWind = dWind;
      }
      if (!isFinite(minVsLee) || !isFinite(minVsWind)) continue;

      let arcVerdict = null;
      const marginLee = minVsWind - minVsLee;
      const marginWind = minVsLee - minVsWind;
      // Very-strong tier first: the rider's heading came close enough to
      // one axis that they must have swept through it. Small margin is
      // enough because a near-axis pass on the OTHER side at the same
      // time would imply a > 180° turn.
      if (minVsLee < veryStrongDeg && marginLee >= veryStrongMargin) arcVerdict = "jibe";
      else if (minVsWind < veryStrongDeg && marginWind >= veryStrongMargin) arcVerdict = "tack";
      // Strong tier: the apex didn't quite reach an axis but the
      // asymmetry is clear (much closer to one side than the other).
      else if (minVsLee < strongDeg && marginLee >= strongMargin) arcVerdict = "jibe";
      else if (minVsWind < strongDeg && marginWind >= strongMargin) arcVerdict = "tack";
      if (!arcVerdict) continue;

      if (m.movementType !== arcVerdict) {
        // Keep `direction` consistent with the new type. The recognizer
        // sets `direction` from the initial leg's relative wind side
        // crossed with the type, so flipping the type flips the
        // direction's interpretation. See `typeOfMovement` for the
        // assignment rules.
        const wasJibe = m.movementType === "jibe";
        const isJibe = arcVerdict === "jibe";
        if (wasJibe !== isJibe && m.direction) {
          m.direction = m.direction === "Starboard" ? "Portboard" : "Starboard";
        }
        m.movementType = arcVerdict;
      }
    }
  }

  /**
   * Bearing between two adjacent track points, gated by a minimum speed
   * on both endpoints so GPS noise at slow speeds doesn't produce
   * meaningless headings. Returns null when the bearing is unreliable.
   *
   * @param {number} i - index of the first point of the pair
   * @return {number|null} bearing in [0, 360) or null
   */
  localBearingAt(i: number): number | null {
    const pts = this.locations;
    const a = pts[i];
    const b = pts[i + 1];
    if (!a || !b) return null;
    const sA = a.speedInKMH != null ? a.speedInKMH : (a.speedInMPS || 0) * 3.6;
    const sB = b.speedInKMH != null ? b.speedInKMH : (b.speedInMPS || 0) * 3.6;
    if ((sA || 0) < MIN_SPEED_FOR_BEARING_KMH) return null;
    if ((sB || 0) < MIN_SPEED_FOR_BEARING_KMH) return null;
    return DirectionUtil.getBearingValue(a.latitude, a.longitude, b.latitude, b.longitude);
  }

  /**
   * Re-align each maneuver's `startIndex` / `endIndex` to the actual U
   * of the turn. The 10-point sliding detector in `parseMovements`
   * commits the first window that crosses the wind axis with a
   * sustained angle change, but that window is rarely the U itself —
   * for short maneuvers (≤ 10 pts) the window pads the U with pre-leg
   * samples, and for long maneuvers (12-25 pts, common on slow rotation
   * sports) the window slices a fixed-width chunk out of the middle of
   * the U and the marker midpoint drifts off the apex.
   *
   * The user-visible symptom is the maneuver being recognized "a few
   * points too early" or "the U is clearly here but you're highlighting
   * the wrong stretch" (windsurf/wing reports, see
   * `tools/measure-window-skew.mjs`). The fix walks outward from the
   * detection window and finds:
   *   - `uStart`: first point whose local bearing diverges from the
   *     sustained pre-leg bearing by >= `_TURN_DIVERGENCE_DEG`. That's
   *     where the rider stopped riding on the entry leg.
   *   - `uEnd`: last point whose local bearing is still > divergence
   *     from the post-leg, found via a stable-run convergence check
   *     (`_STABLE_RUN` consecutive samples within `_TURN_CONVERGE_DEG`
   *     of the post-leg bearing). That's where the rider settled on
   *     the exit leg.
   *
   * Both bounds are clamped by `_WINDOW_MAX_EXPAND_BACKWARD` /
   * `_WINDOW_MAX_EXPAND_FORWARD` so we never grab adjacent legs even
   * when the pre/post sustained bearing is unreliable. When the legs
   * aren't measurable (start/end of track, low-speed samples), we keep
   * the original window — the alignment never DROPS a maneuver.
   *
   * This pass runs AFTER all classification (so flipping start/end
   * doesn't change tack-vs-jibe verdicts) but BEFORE the
   * stop-and-restart pass (so bridge maneuvers, which have their own
   * meaningful endpoints, aren't touched).
   *
   * @param {Array<Movement>} movements - mutated in place
   */
  alignManeuverWindowsToTurn(movements: Movement[]): void {
    if (!movements || movements.length === 0) return;
    const pts = this.locations;
    if (!pts || pts.length < 4) return;
    const probe = new Movement(this.windDirection, this.sport);
    const thresholds = probe.getSportThresholds(this.sport);
    const ridingKmh = thresholds.exitSpeed;
    const DIVERGENCE = TACK_JIBE_TURN_DIVERGENCE_DEG;
    const CONVERGE = TACK_JIBE_TURN_CONVERGE_DEG;
    const STABLE_RUN = TACK_JIBE_TURN_STABLE_RUN;
    const MAX_BACK = TACK_JIBE_WINDOW_MAX_EXPAND_BACKWARD;
    const MAX_FWD = TACK_JIBE_WINDOW_MAX_EXPAND_FORWARD;
    const MIN_WIDTH = TACK_JIBE_WINDOW_MIN_WIDTH;

    for (let mi = 0; mi < movements.length; mi++) {
      const m = movements[mi];
      if (!m || m.startIndex == null) continue;
      // Stop-and-restart bridges have meaningful endpoints (start of
      // the pause vs resumption); aligning them to in-pause bearings
      // would break them.
      if (m.isStopAndRestart) continue;

      const origStart = m.startIndex;
      const origEnd = this._movementEndIndex(m);

      // Same boundary rationale as `filterPhantomManeuvers`: don't let
      // the leg measurement cross into a neighboring detected maneuver,
      // or `alignManeuverWindowsToTurn` will reach the neighbor's turn
      // dynamics and decide the rider "settled" on a heading that is
      // actually still rotating. As with the phantom filter, we only
      // clamp against STRONG neighbours; any weak detection that
      // slipped through earlier passes is skipped so its
      // near-zero-width gap doesn't collapse the alignment search.
      const prevStrong = this._nearestStrongNeighbor(movements, mi, -1);
      const nextStrong = this._nearestStrongNeighbor(movements, mi, +1);
      const preLegBoundary = prevStrong ? this._movementEndIndex(prevStrong) : null;
      const postLegBoundary = nextStrong ? nextStrong.startIndex : null;

      const preLeg = this.sustainedLegBearing(
        origStart - 1,
        -1,
        TACK_JIBE_SUSTAINED_LAG_SEC,
        TACK_JIBE_SUSTAINED_DURATION_SEC,
        ridingKmh,
        preLegBoundary
      );
      const postLeg = this.sustainedLegBearing(
        origEnd + 1,
        +1,
        TACK_JIBE_SUSTAINED_LAG_SEC,
        TACK_JIBE_SUSTAINED_DURATION_SEC,
        ridingKmh,
        postLegBoundary
      );
      // Need at least one anchor to do anything meaningful. If neither
      // leg is measurable the detection-window indices are the best we
      // have — don't touch them.
      if (preLeg == null && postLeg == null) continue;

      // Clamp the alignment sweep so it can't expand into a neighbor's
      // turn — without this, two tight back-to-back maneuvers would
      // both reach into the inter-maneuver leg and the divergence /
      // convergence checks would pick up the neighbor's rotation
      // instead of this maneuver's own.
      const prevBoundary = preLegBoundary != null ? preLegBoundary + 1 : 0;
      const nextBoundary = postLegBoundary != null ? postLegBoundary - 1 : pts.length - 2;
      const searchFrom = Math.max(0, prevBoundary, origStart - MAX_BACK);
      const searchTo = Math.min(pts.length - 2, nextBoundary, origEnd + MAX_FWD);

      // ---- forward sweep: find the first index where the rider's
      // heading has clearly left the pre-leg.
      let uStart = origStart;
      if (preLeg != null) {
        let foundDivergence = false;
        for (let i = searchFrom; i <= searchTo; i++) {
          const b = this.localBearingAt(i);
          if (b == null) continue;
          if (probe.bearingDistance(b, preLeg) >= DIVERGENCE) {
            uStart = i;
            foundDivergence = true;
            break;
          }
        }
        // If the pre-leg bearing never diverges across the entire
        // search range, the pre-leg measurement is probably noisy —
        // keep the detector's startIndex rather than collapsing the
        // maneuver to zero width.
        if (!foundDivergence) uStart = origStart;
      }

      // ---- forward sweep: find where the rider settled on the
      // post-leg. We require `STABLE_RUN` consecutive close-to-post
      // samples to confirm we're not just passing through the post
      // heading mid-carve.
      let uEnd = origEnd;
      if (postLeg != null) {
        let settled = -1;
        for (let i = uStart + 1; i <= searchTo; i++) {
          let stable = true;
          for (let k = 0; k < STABLE_RUN; k++) {
            const b = this.localBearingAt(i + k);
            if (b == null) {
              stable = false;
              break;
            }
            if (probe.bearingDistance(b, postLeg) >= CONVERGE) {
              stable = false;
              break;
            }
          }
          if (stable) {
            settled = i;
            break;
          }
        }
        // `settled` is the FIRST point on the new leg, so the U's
        // last sample is the one right before it.
        if (settled > uStart) uEnd = settled - 1;
        // If we never settled, the post-leg is still away — keep the
        // detector's endIndex as a conservative ceiling.
        else uEnd = origEnd;
      }

      // Final sanity: keep the window at least `_MIN_WIDTH` wide and
      // never invert it. If the alignment would collapse it below
      // MIN_WIDTH, leave the original (better to be slightly wrong
      // than to lose downstream metrics).
      if (uEnd < uStart) continue;
      if (uEnd - uStart + 1 < MIN_WIDTH) continue;

      m.startIndex = uStart;
      m.endIndex = uEnd;
    }
  }

  removeDuplicatePoints(tolerance: number = DUPLICATE_POINT_TOLERANCE): void {
    if (this.locations.length <= 1) return;

    const uniqueLocations = [this.locations[0]]; // Always keep the first point

    for (let i = 1; i < this.locations.length; i++) {
      const current = this.locations[i];
      const lastUnique = uniqueLocations[uniqueLocations.length - 1];

      const latDiff = Math.abs(current.latitude - lastUnique.latitude);
      const lonDiff = Math.abs(current.longitude - lastUnique.longitude);

      if (latDiff > tolerance || lonDiff > tolerance) {
        uniqueLocations.push(current);
      }
    }

    this.locations = uniqueLocations;
  }

  // Stop-and-restart maneuver detection.
  //
  // The regular `parseMovements()` slides a 10-point window over the track
  // and never sees across long pauses. When a windsurfer/winger falls or
  // takes a break and water-starts in the opposite direction, the heading
  // flip can span 1–3 minutes of essentially-stationary points; the sliding
  // window can't bridge that, so the user's real "I tacked but fell" event
  // never appears in the maneuver list.
  //
  // This pass identifies long pauses (rider clearly stopped, not just slow
  // through a tack apex) and emits one Movement per pause whose pre/post
  // sustained headings cross the wind axis. We deliberately gate hard on:
  //   - pause duration (>= MIN_PAUSE_SEC)
  //   - sustained near-zero speed inside the pause (rider was actually
  //     stopped, not just decelerating through a normal tack)
  //   - heading change >= MIN_BRIDGE_ANGLE (substantial turn, not stall)
  //   - both pre- and post-pause segments at riding speed
  //   - non-overlap with already-detected continuous maneuvers
  // so that normal tacks (which the regular pass already finds) aren't
  // double-counted. Bridge maneuvers are flagged `isStopAndRestart = true`
  // and `successful = false` (by definition the rider lost speed).
  findStopAndRestartManeuvers(): Movement[] {
    const result = [];
    const pts = this.locations;
    if (!pts || pts.length < this.pointsToUse) return result;

    const probe = new Movement(this.windDirection, this.sport);
    const thresholds = probe.getSportThresholds(this.sport);
    const RIDING_KMH = thresholds.avgSpeed;
    const STOPPED_KMH = 1.5; // walking pace - below this rider is not on board
    const MIN_PAUSE_SEC = 30; // long enough to exclude normal tack apex slowdowns
    const MIN_STOPPED_SEC = 5; // some part of the pause must be truly stopped
    const SUSTAIN_SEC = 5; // window for "sustained riding" check
    const NEIGH = 5; // points to average for pre/post heading
    // Higher than TACK_JIBE_MIN_ANGLE because the regular detector is allowed
    // tiny turns (course corrections); a stop-and-restart that we want to
    // count as a maneuver should be a substantial turn, not a brief stall.
    const MIN_BRIDGE_ANGLE = 60;
    const adjustedWind = probe.adjustWind(this.windDirection);

    // For each point, is the next SUSTAIN_SEC of points avg-riding?
    const isRiding = new Array(pts.length).fill(false);
    for (let i = 0; i < pts.length; i++) {
      let j = i;
      let sum = 0;
      let cnt = 0;
      while (j < pts.length && pts[j].unixTimestamp - pts[i].unixTimestamp < SUSTAIN_SEC) {
        sum += pts[j].speedInKMH || 0;
        cnt++;
        j++;
      }
      if (cnt > 0 && sum / cnt >= RIDING_KMH) isRiding[i] = true;
    }

    const sustainedHeading = (centerIdx, dir) => {
      // dir = -1 walks backwards (pre-pause), +1 walks forwards (post-pause)
      const collected = [];
      let j = centerIdx;
      while (collected.length < NEIGH && j >= 0 && j < pts.length) {
        if ((pts[j].speedInKMH || 0) >= RIDING_KMH) collected.push(pts[j]);
        j += dir;
      }
      if (collected.length < 2) return null;
      if (dir < 0) collected.reverse();
      const a = collected[0];
      const b = collected[collected.length - 1];
      return {
        h: DirectionUtil.getBearingValue(a.latitude, a.longitude, b.latitude, b.longitude),
        spd: collected.reduce((s, p) => s + (p.speedInKMH || 0), 0) / collected.length
      };
    };

    let i = 0;
    while (i < pts.length) {
      if (isRiding[i]) {
        i++;
        continue;
      }
      const start = i;
      while (i < pts.length && !isRiding[i]) i++;
      const end = i - 1;
      if (start === 0 || end >= pts.length - 1) continue; // need riding on both sides
      const dur = pts[end].unixTimestamp - pts[start].unixTimestamp;
      if (dur < MIN_PAUSE_SEC) continue;

      // Require a real stop somewhere inside (not just slow rolling through).
      let stoppedDur = 0;
      let stoppedRunStart = -1;
      let maxStopped = 0;
      for (let k = start; k <= end; k++) {
        if ((pts[k].speedInKMH || 0) < STOPPED_KMH) {
          if (stoppedRunStart < 0) stoppedRunStart = k;
          stoppedDur = pts[k].unixTimestamp - pts[stoppedRunStart].unixTimestamp;
          if (stoppedDur > maxStopped) maxStopped = stoppedDur;
        } else {
          stoppedRunStart = -1;
          stoppedDur = 0;
        }
      }
      if (maxStopped < MIN_STOPPED_SEC) continue;

      // Sustained heading just before the pause and just after.
      const before = sustainedHeading(start - 1, -1);
      const after = sustainedHeading(end + 1, +1);
      if (!before || !after) continue;
      if (Math.max(before.spd, after.spd) < RIDING_KMH) continue;

      const angChg = probe.bearingDistance(before.h, after.h);
      if (angChg < MIN_BRIDGE_ANGLE) continue;

      // Wind crossing test (same as regular detection).
      const initRel = probe.calculateRelativeAngle(before.h, adjustedWind);
      const finalRel = probe.calculateRelativeAngle(after.h, adjustedWind);
      if (initRel * finalRel >= 0) continue;

      // Skip if a regular maneuver was already detected covering this pause.
      const overlap = this.movements.some((mv) => {
        const ms = mv.startIndex ?? 0;
        const me = mv.endIndex ?? ms + this.pointsToUse;
        return !(me < start || ms > end);
      });
      if (overlap) continue;

      // Build a Movement representing the bridge maneuver. We use the points
      // spanning the pause (with pre- and post-neighbors) so downstream UIs
      // can render the full turn arc.
      const sliceStart = Math.max(0, start - NEIGH);
      const sliceEnd = Math.min(pts.length - 1, end + NEIGH);
      const movement = new Movement(this.windDirection, this.sport, pts.slice(sliceStart, sliceEnd + 1));
      movement.startIndex = sliceStart;
      movement.endIndex = sliceEnd;
      movement.angle = angChg;
      movement.calculateSpeeds();
      // Bridge maneuvers are by definition unsuccessful (rider lost speed).
      movement.successful = false;
      movement.isStopAndRestart = true;
      // Classify by where the rider's path swept relative to the leeward axis,
      // using the same path-mid logic as continuous maneuvers. We don't have
      // a real `directionBearing` (no continuous samples through the turn),
      // so use the start-to-end signed delta to pick the shorter arc.
      const fakeDirBearing = probe.normalize(before.h + probe.signedBearingDelta(before.h, after.h) / 2);
      movement.classifyMovement(before.h, after.h, adjustedWind, fakeDirBearing);
      const directionInitial = movement.calculateDirection(adjustedWind, before.h);
      if (movement.movementType === "jibe") {
        movement.direction = directionInitial === "Portboard" ? "Starboard" : "Portboard";
      } else {
        movement.direction = directionInitial === "Starboard" ? "Starboard" : "Portboard";
      }
      result.push(movement);
    }
    return result;
  }

  parseMovements(): Movement[] {
    const movements = [];
    let startIndex = 0;
    let maxAngle = 0.0;
    let prevType = "none";
    let prevMove = new Movement(this.windDirection, this.sport);

    while (startIndex < this.locations.length - 1) {
      const endIndex = startIndex + this.pointsToUse;
      if (endIndex <= this.locations.length) {
        const locs = this.locations.slice(startIndex, endIndex);
        const movement = new Movement(this.windDirection, this.sport, locs);

        const movementType = movement.typeOfMovement();
        if (movementType !== "none") {
          // If a valid movement is detected
          if (movement.angle < maxAngle && maxAngle > 0) {
            movement.setLocs(locs);
            movement.movementType = movementType;
            movement.startIndex = startIndex;
            movements.push(movement);

            startIndex = endIndex - 2;
            prevType = "none";
            prevMove = new Movement(this.windDirection, this.sport);
            maxAngle = 0;
          } else {
            movement.setLocs(locs);
            movement.startIndex = startIndex;
            movement.endIndex = endIndex;
            prevMove = movement;
            prevType = movementType;
            maxAngle = movement.angle;
          }
        } else {
          if (prevType !== "none") {
            prevMove.setLocs(this.locations.slice(startIndex - 1, endIndex - 1));
            prevMove.movementType = prevType;
            prevMove.startIndex = startIndex;
            movements.push(prevMove);

            startIndex = endIndex - 2;
          }
          prevType = "none";
          prevMove = new Movement(this.windDirection, this.sport);
          maxAngle = 0;
        }
      }

      startIndex += 1;
    }

    const deduped = this._mergeDuplicateMovements(movements);
    return this._reclassifyIsolatedMovements(deduped);
  }

  /**
   * Collapse re-detections of the same physical maneuver.
   *
   * The sliding-window detector in `parseMovements` can fire 2–3 times on
   * a single slow turn: after each push it advances by only
   * ~`pointsToUse - 1` points and resets `maxAngle = 0`, so a long
   * turn (common on wing/wingfoil sessions, especially with irregular
   * GPX sampling) re-arms and is matched again by a later window slice.
   * The result is a cluster of "Tack"/"Jibe" markers stacked on the same
   * physical maneuver.
   *
   * Heuristic — group adjacent detections that have:
   *   - the same `movementType` (both tacks or both jibes), AND
   *   - the same `direction` (both record the same `directionInitial`
   *     side; this is the reliable signal that they describe the same
   *     turn — two REAL consecutive maneuvers always flip the boat,
   *     so their recorded `direction` differs), AND
   *   - a startIndex gap below `TACK_JIBE_MIN_SEPARATION` from the
   *     previous item in the group (chaining handles 3+ duplicates).
   *
   * Within each group we keep the detection with the largest `angle`,
   * because the algorithm's "decline" branch tends to push *after* the
   * peak and the max-angle window is the closest in time to the actual
   * maneuver center.
   *
   * @param {Movement[]} movements - Sorted-by-startIndex maneuver list
   *   produced by `parseMovements`.
   * @return {Movement[]} Deduplicated maneuver list (input is left
   *   untouched; same or shorter length is returned).
   */
  _mergeDuplicateMovements(movements: Movement[]): Movement[] {
    if (!movements || movements.length <= 1) return movements;

    const result = [];
    let i = 0;
    while (i < movements.length) {
      let bestIdx = i;
      let j = i + 1;
      while (j < movements.length) {
        const prev = movements[j - 1];
        const cur = movements[j];
        const sameType = cur.movementType === prev.movementType;
        const sameDirection = cur.direction === prev.direction;
        const close = cur.startIndex - prev.startIndex < TACK_JIBE_MIN_SEPARATION;
        if (!(sameType && sameDirection && close)) break;
        if (cur.angle > movements[bestIdx].angle) {
          bestIdx = j;
        }
        j += 1;
      }
      result.push(movements[bestIdx]);
      i = j;
    }
    return result;
  }

  /**
   * On board sports, a high-angle jibe flanked by two tacks on an upwind beat
   * is almost always a misclassified tack (slow apex, no dead-downwind pass).
   *
   * @param {Movement[]} movements
   * @return {Movement[]}
   */
  _reclassifyJibesBetweenTacks(movements: Movement[]): Movement[] {
    if (!movements || movements.length < 3) return movements;
    if (isSailingSport(this.sport)) return movements;

    const flipDirection = (dir) => (dir === "Starboard" ? "Portboard" : dir === "Portboard" ? "Starboard" : dir);
    const maxGap = TACK_JIBE_MIN_SEPARATION * 5;

    for (let i = 1; i < movements.length - 1; i++) {
      const m = movements[i];
      const prev = movements[i - 1];
      const next = movements[i + 1];
      if (!m || m.movementType !== "jibe") continue;
      if (prev?.movementType !== "tack" || next?.movementType !== "tack") continue;
      if (m.startIndex - prev.startIndex > maxGap || next.startIndex - m.startIndex > maxGap) continue;
      if ((m.angle || 0) < 60) continue;
      m.movementType = "tack";
      if (m.direction) m.direction = flipDirection(m.direction);
    }

    return movements;
  }

  /**
   * Probabilistic fix-up for isolated tack/jibe misclassifications.
   *
   * The upstream `classifyMovement` decides "tack vs jibe" from the
   * absolute angle between bearing and wind. On windsurf / wing / kite
   * / foil the GPS bearing can jitter ±30° during a single planing
   * turn, occasionally pushing one detection across the
   * upwind/downwind threshold and flipping its label even though the
   * rider only ever did jibes (or only ever did tacks) on either side
   * of it.
   *
   * Physical-plausibility rule used here: on a board sport, if a
   * single maneuver sits between TWO same-type maneuvers on each side
   * (4 immediate neighbors, all of the same type, different from the
   * middle one), the middle one is almost certainly the same type as
   * its neighbors and is relabeled to match. The 4-unanimous gate is
   * intentionally strict so we only fix obvious one-off
   * misclassifications and never override a real isolated maneuver of
   * the other type.
   *
   * Sport scope: skipped for sailing (IDs 1, 26), because sailboats
   * legitimately interleave tacks and jibes in a single course
   * (e.g. an upwind beat with a single jibe to duck a mark).
   *
   * Side-effect on `direction`: `direction` is derived from
   * `directionInitial` differently for tacks (`= initial side`) and
   * jibes (`= opposite of initial side`), so flipping the type
   * without recomputing means we must also flip `direction`
   * Starboard ↔ Portboard to keep the displayed Port/Starboard label
   * (`ShowTackButton`, `ShowJibeButton`, `ManeuverItem`, …) consistent
   * with the new classification.
   *
   * Order matters: must run AFTER `_mergeDuplicateMovements` so that
   * the "neighbors" we vote against are 4 distinct physical turns,
   * not duplicate detections of the same turn.
   *
   * @param {Movement[]} movements
   * @return {Movement[]} Same array reference, with `.movementType`
   *   and `.direction` flipped on isolated misclassifications.
   */
  _reclassifyIsolatedMovements(movements: Movement[]): Movement[] {
    if (!movements || movements.length < 5) return movements;
    if (isSailingSport(this.sport)) return movements;

    // Collect changes first; applying them in-place during the scan
    // would let an earlier flip influence a later vote and cascade
    // unrelated maneuvers.
    const flipDirection = (dir) => (dir === "Starboard" ? "Portboard" : dir === "Portboard" ? "Starboard" : dir);
    const flips = [];
    for (let i = 2; i < movements.length - 2; i++) {
      const neighborTypes = [
        movements[i - 2].movementType,
        movements[i - 1].movementType,
        movements[i + 1].movementType,
        movements[i + 2].movementType
      ];
      const target = neighborTypes[0];
      const unanimous = target !== "none" && neighborTypes.every((t) => t === target);
      if (unanimous && movements[i].movementType !== target) {
        flips.push({index: i, newType: target});
      }
    }

    flips.forEach(({index, newType}) => {
      const m = movements[index];
      m.movementType = newType;
      m.direction = flipDirection(m.direction);
    });

    return movements;
  }

  getMovements(movementType: string): Movement[] {
    return this.movements
      .filter((movement) => movement?.movementType === movementType)
      .map((movement, index) => {
        movement.index = index + 1;
        return movement;
      });
  }

  getTacks(): Movement[] {
    return this.getMovements("tack");
  }

  getJibes(): Movement[] {
    return this.getMovements("jibe");
  }

  /**
   * Convert the auto-detected, successful movements into the wire format
   * the maneuver-override backend expects:
   *   { start_point: int, end_point: int, type: "tack"|"jibe" }
   *
   * This is the bridge between the in-memory `Movement` model and the
   * persisted SQL store. It keeps only successful maneuvers, sorts them by
   * start point, and clamps end_point >= start_point.
   *
   * @return {Array<{start_point:number,end_point:number,type:string}>} list
   */
  toOverrideList(): ManeuverOverride[] {
    return this.movements
      .filter((m) => m && m.successful && m.movementType && m.startIndex >= 0)
      .map((m) => {
        const start = m.startIndex;
        const end =
          m.endIndex !== undefined && m.endIndex > m.startIndex
            ? m.endIndex
            : m.startIndex + Math.max(0, (m.locations?.length || 1) - 1);
        return {
          start_point: start,
          end_point: Math.max(start, end),
          type: m.movementType
        };
      })
      .sort((a, b) => a.start_point - b.start_point);
  }
}

function maneuverIndexRangesOverlap(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
  return aStart <= bEnd && bStart <= aEnd;
}

export class OverriddenTacksAndJibes {
  locations: TrackPoint[];
  windDirection: number;
  sport: number;
  overrides: ManeuverOverride[];
  movements: Movement[];

  constructor(locations: TrackPoint[], windDirection: number, sport: number, overrides: ManeuverOverride[] = []) {
    this.locations = Array.isArray(locations) ? locations : [];
    this.windDirection = windDirection;
    this.sport = sport;
    this.overrides = Array.isArray(overrides) ? overrides : [];
    this.movements = this.buildMovements();
  }

  buildOverrideMovements(): Movement[] {
    const points = this.locations;
    const maxIndex = points.length - 1;
    if (maxIndex < 0) return [];

    return this.overrides
      .map((ov) => {
        const start = Math.max(0, Math.min(maxIndex, Number(ov.start_point)));
        const end = Math.max(start, Math.min(maxIndex, Number(ov.end_point)));
        const type = ov?.type === "tack" || ov?.type === "jibe" ? ov.type : null;
        if (type === null) return null;

        const slice = points.slice(start, end + 1);
        if (slice.length === 0) return null;

        const movement = new Movement(this.windDirection, this.sport, [], type);
        // Use setLocs so duplicate-point pruning + endpoints get computed.
        movement.setLocs(slice);
        movement.movementType = type;
        movement.startIndex = start;
        movement.endIndex = end;

        // Compute derived stats so downstream UIs (cards, polar filtering,
        // VMG callouts, etc.) have the same fields the auto-recognizer
        // produces. Anything that requires the full geometric classification
        // is computed best-effort; user-defined maneuvers are always
        // considered "successful" so they show up everywhere.
        movement.calculateSpeeds();

        const pts = movement.locations;
        if (pts.length >= 2) {
          const initialBearing = movement.calculateBearing(pts[0], pts[Math.max(1, Math.floor(pts.length / 3))]);
          const finalBearing = movement.calculateBearing(
            pts[Math.min(pts.length - 2, pts.length - Math.floor(pts.length / 3) - 1)],
            pts[pts.length - 1]
          );
          movement.angle = movement.angleDifference(initialBearing, finalBearing);

          const adjustedWind = movement.adjustWind(this.windDirection);
          const directionFinal = movement.calculateDirection(adjustedWind, finalBearing);
          movement.direction = directionFinal || "Starboard";
        }

        movement.successful = true;
        movement.isOverride = true;
        movement.isUserDefined = ov?.is_user_defined === true;
        movement.overrideId = ov?.id ?? null;
        return movement;
      })
      .filter(Boolean)
      .sort((a, b) => a.startIndex - b.startIndex);
  }

  buildMovements(): Movement[] {
    const points = this.locations;
    if (points.length === 0) return [];

    const overrideMovements = this.buildOverrideMovements();
    if (overrideMovements.length === 0) {
      return [];
    }

    // SQL only stores successful maneuvers. Re-run auto-detection so missed
    // attempts are available for % missed stats and downstream consumers that
    // read unsuccessful maneuvers from getTacks()/getJibes().
    const overrideRanges = overrideMovements.map((m) => ({
      start: m.startIndex,
      end: m.endIndex
    }));
    const autoRecognizer = new TacksAndJibesRecognizer(points, this.windDirection, this.sport);
    const missedAuto = autoRecognizer.movements.filter((m) => {
      if (!m || m.successful) return false;
      const start = m.startIndex ?? 0;
      const end = m.endIndex ?? start;
      return !overrideRanges.some((r) => maneuverIndexRangesOverlap(start, end, r.start, r.end));
    });

    return [...overrideMovements, ...missedAuto].sort((a, b) => a.startIndex - b.startIndex);
  }

  getMovements(movementType: string): Movement[] {
    return this.movements
      .filter((m) => m?.movementType === movementType)
      .map((m, index) => {
        m.index = index + 1;
        return m;
      });
  }

  getTacks(): Movement[] {
    return this.getMovements("tack");
  }

  getJibes(): Movement[] {
    return this.getMovements("jibe");
  }
}

/**
 * EMPTY_TACKS_AND_JIBES_SERVICE
 *
 * Placeholder used as `activity.tacksAndJibesService` while the SQL cloud
 * load is in flight. We deliberately do NOT run the local recognizer
 * pre-emptively: the SQL store is the source of truth, so the UI should
 * show "no maneuvers yet" for the brief window between activity-parse and
 * the SQL fetch resolving, instead of briefly displaying locally-computed
 * tacks/jibes that may then get overwritten by SQL data.
 *
 * The local recognizer is only used as a fallback when both the SQL fetch
 * and the cloud `recognizeManeuvers` Callable fail.
 *
 * Implements the same interface as TacksAndJibesRecognizer /
 * OverriddenTacksAndJibes that downstream consumers call.
 *
 * Frozen so callers can never accidentally mutate it (it's a singleton
 * shared across every freshly-parsed activity).
 */
export const EMPTY_TACKS_AND_JIBES_SERVICE = Object.freeze({
  movements: Object.freeze([]),
  getMovements() {
    return [];
  },
  getTacks(): Movement[] {
    return [];
  },
  getJibes(): Movement[] {
    return [];
  },
  toOverrideList() {
    return [];
  }
});
